/* @flow */
/* eslint-env es6,browser */

// set global variable needed
import bidbase, { INIT_STATUS } from './bidbase';
import { logError } from '../utils/logger';
import { centsToDollar, extend, isNumber } from '../utils/coreutils';
import { getReferrer, isJSONSupported } from '../utils/browserutil';
import { getRandom } from '../utils';
import { fetchJSON, isFetchSupported } from '../utils/fetch';
import {
  PREBID_UID_FABRICK,
  PREBID_UID_ID5ID,
  PREBID_UID_LIVERAMP,
  PREBID_UID_VERIZON
} from '../idproviders/idpconstants';
import { getRTBUserEids } from '../idproviders/idputils';
import adconfig from '../adconfig';

/**
 * @typedef {object} hbPosData
 */

const BID_URL = 'https://htlb.casalemedia.com/cygnus';

/**
 * @module bidders/index
 * @requires module:logger
 * @requires module:coreutils
 */
/**
 * @global
 * @typedef {object} indexConfig
 * @description extends {@link hbConfig}
 * @property {number} id - ID
 */
/**
 * @description Implements functionality for index (casalemedia) header bidding
 * @augments _bidbase
 * @requires module:utils
 */
class _indexbid extends bidbase {
  _config: indexConfig;

  _DefConfig: indexConfig;

  constructor(name: string = 'index') {
    super(name);
    this._DefConfig = extend(true, {}, this._DefConfig, {
      id: 0
    });
    this._config = extend(true, {}, this._DefConfig);
    this._isSupported = isJSONSupported() && isFetchSupported();
  }

  /**
   * - Checks partner specific config for index partner
   *
   * @typedef {object} initStatus
   * @returns {initStatus} - Init status
   * @access private
   */
  _configCheck(): initStatus {
    // id should be a number (default set to zero) - index javascript checks for id being number
    if (!isNumber(this._config.id) || this._config.id === 0) {
      logError(`${this._name} config error: missing mandatory data`);
      return INIT_STATUS.F_FAILED;
    }

    // index js expect height, weight & siteID as number
    const props = ['height', 'width', 'siteID'];
    const chk = Object.keys(this._config.posTokenMap).every((pos: string) =>
      props.every(prop => {
        if (!isNumber(this._config.posTokenMap[pos][prop])) {
          logError(
            `${this._name} config error: posTokenMap for ${pos} for property ${prop} is not number`
          );
          return false;
        }
        return true;
      })
    );

    return chk ? super._configCheck() : INIT_STATUS.F_FAILED;
  }

  /**
   * @param {Array<string>} posList - List of HB positions in response
   * @param {object} resp - Response object from back-end
   * @param {object.<string, hbPosData>} ret - Return data
   * @param {string} bidReqID - Request ID
   * @param {object} impIDMap - Impression ID map
   * @returns {object.<string, hbPosData>} - Response after processing
   * @access private
   */
  _processBidResponse(
    posList: Array<string>,
    resp: RTBBidResponse,
    ret: hbPartnerData,
    bidReqID: string,
    // flowlint-next-line unclear-type:off
    impIDMap: Object
  ): hbPartnerData {
    if (!this._isValidRTBResponse(resp) || resp.id !== bidReqID) {
      return ret;
    }

    const bids = resp.seatbid.flatMap(v => v.bid);
    const bidResposeMap = {};

    bids.forEach(bid => {
      if (bid.impid) {
        bidResposeMap[bid.impid] = bid;
      }
    });

    // For IX, we are getting bid price in cents. Convert to $
    posList.forEach(pos => {
      const impid = impIDMap[pos];
      const curBid = bidResposeMap[impid] || {};
      const { adm = '', nurl = '' } = curBid;
      let { price = '' } = curBid;
      price = String(price);
      if (price.length > 0 && adm.length > 0) {
        this._handleBidPosData(
          ret,
          pos,
          centsToDollar(Number(price)),
          impid,
          adm,
          nurl || ''
        );
      }
    });
    return ret;
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB ad positions list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    const conf = this._config;
    // generate unique request ID
    const bidReqID = getRandom();
    const impIDMap = {};

    // build param for bid request
    const bid = {
      id: bidReqID,
      site: {
        page: getReferrer(),
        ref: document.referrer || ''
      },
      ext: {
        source: 'prebid'
      },
      regs: {
        ext: this._getPrivacyString()
      },
      imp: posList.map(pos => {
        // generate unique id for this impression(position)
        const id = getRandom();
        impIDMap[pos] = id;
        const gpid = adconfig.getGPID(pos);
        return {
          id,
          ext: {
            // eslint-disable-next-line camelcase
            dfp_ad_unit_code: gpid,
            gpid,
            siteID: conf.posTokenMap[pos].siteID,
            sid: `${conf.posTokenMap[pos].width}x${conf.posTokenMap[pos].height}`
          },
          banner: {
            w: conf.posTokenMap[pos].width,
            h: conf.posTokenMap[pos].height,
            topframe: 1
          }
        };
      })
    };
    const { siteID } = bid.imp[0].ext;
    const data = {
      ac: 'j',
      sd: 1,
      v: 7.2,
      s: siteID,
      r: JSON.stringify(
        extend(
          {},
          bid,
          getRTBUserEids([
            PREBID_UID_LIVERAMP,
            PREBID_UID_FABRICK,
            PREBID_UID_ID5ID,
            PREBID_UID_VERIZON
          ])
        )
      )
    };
    const logPrefix = posList.join(',');
    return fetchJSON(
      BID_URL,
      {
        logPrefix,
        data,
        timeout: this._config.timeout,
        format: 'json'
      },
      {
        credentials: 'include'
      }
    ).then(
      (resData: RTBBidResponse) =>
        this._processBidResponse(posList, resData, ret, bidReqID, impIDMap),
      error => {
        logError(`${this._name} Failed to get bids: ${error || ''}`);
        return ret;
      }
    );
  }
}

const indexbid: _indexbid = new _indexbid();
export default indexbid;
